import { Reimbursement } from '@kalos/kalos-rpc';

import { type DirtyModelFields, timestamp } from '../../../tools/helpers';
import { getFieldMaskFromDirtyField } from '../../ComponentsLibrary/EmployeeDepartments/utils';

/** One action a reimbursement row offers. `gallery` is View Photos. */
export type ReimbursementActionKey =
  | 'gallery'
  | 'edit'
  | 'delete'
  | 'submit'
  | 'approve'
  | 'reject'
  | 'process';

/**
 * Which actions a reimbursement offers, in the order desktop's table shows them, from its status
 * and the viewer's roles. Shared by the desktop table and the phone record so both follow one rule.
 *
 * Pending (1) reproduces desktop exactly, including `isOwner || (isManager && …)`: for the owner
 * that expression is `true`, which React renders as nothing, so the owner of a pending
 * reimbursement gets only the photos (ClickUp 86bcdb9bp). Processed (5) and any other status get
 * no actions at all, photos included, as on desktop.
 */
export const getReimbursementActions = ({
  statusId,
  isOwner,
  isManager,
  isPayroll,
}: {
  statusId: Reimbursement['statusId'];
  isOwner: boolean;
  isManager: boolean;
  isPayroll: boolean;
}): ReimbursementActionKey[] => {
  // pending
  if (statusId == 1) {
    return ['gallery', ...(!isOwner && isManager ? (['edit', 'delete', 'submit'] as const) : [])];
  }

  // submitted
  if (statusId == 2) {
    return [
      'gallery',
      ...(isOwner || isManager ? (['edit', 'delete'] as const) : []),
      ...(isManager && !isOwner ? (['approve', 'reject'] as const) : []),
    ];
  }

  // approved
  if (statusId == 3) {
    return [
      'gallery',
      ...(isManager ? (['reject'] as const) : []),
      ...(isPayroll ? (['process'] as const) : []),
    ];
  }

  // rejected
  if (statusId == 4) {
    return ['gallery', ...(isOwner || isManager ? (['edit', 'delete', 'submit'] as const) : [])];
  }

  return [];
};

/** Process stays visible but can't be used once payroll has processed the reimbursement. */
export const isReimbursementAlreadyProcessed = (r: Pick<Reimbursement, 'payrollProcessed'>) =>
  r.payrollProcessed == true;

/** Desktop's confirmation questions and toasts, word for word. */
export const REIMBURSEMENT_ACTION_COPY = {
  edit: { success: 'Reimbursement Updated', error: 'Error Updating Reimbursement' },
  process: {
    confirm: 'Are you sure you want to process this reimbursement?',
    success: 'Reimbursement Processed',
    error: 'Error Processing Reimbursement',
  },
  approve: {
    confirm: 'Are you sure you want to approve this reimbursement?',
    success: 'Reimbursement Approved',
    error: 'Error Approving Reimbursement',
  },
  delete: {
    confirm: 'Are you sure you want to delete this reimbursement?',
    success: 'Reimbursement Deleted',
    error: 'Error Deleting Reimbursement',
  },
  reject: {
    confirm: 'Are you sure you want to reject this reimbursement?',
    success: 'Reimbursement Rejected',
    error: 'Error Rejecting Reimbursement',
  },
  submit: {
    confirm: 'Are you sure you want to submit this reimbursement?',
    success: 'Reimbursement Submitted',
    error: 'Error Submitting Reimbursement',
  },
} as const;

// The requests each action sends. Built when the person confirms, so timestamps are that moment.

/** The fields Edit changes: the edit form's values. */
export type ReimbursementEditValues = {
  jobNumber: number;
  userId: number;
  amount: number;
  description: string;
};

export const buildReimbursementEditRequest = (
  id: Reimbursement['id'],
  data: ReimbursementEditValues,
  dirtyFields: DirtyModelFields<ReimbursementEditValues>,
) =>
  Reimbursement.create({
    id,
    ...data,
    fieldMask: getFieldMaskFromDirtyField(dirtyFields),
  });

export const buildReimbursementProcessRequest = (r: Pick<Reimbursement, 'id'>) =>
  Reimbursement.create({
    id: r.id,
    payrollProcessed: true,
    statusId: 5,
    processedDate: timestamp(),
    fieldMask: ['PayrollProcessed', 'StatusId', 'ProcessedDate'],
  });

export const buildReimbursementApproveRequest = (
  r: Pick<Reimbursement, 'id'>,
  reviewerId: Reimbursement['reviewerId'],
) =>
  Reimbursement.create({
    id: r.id,
    statusId: 3,
    reviewerId,
    reviewDate: timestamp(),
    fieldMask: ['StatusId', 'ReviewerId', 'ReviewDate'],
  });

export const buildReimbursementDeleteRequest = (r: Pick<Reimbursement, 'id'>) =>
  Reimbursement.create({
    id: r.id,
  });

export const buildReimbursementRejectRequest = (r: Pick<Reimbursement, 'id'>) =>
  Reimbursement.create({
    id: r.id,
    statusId: 4,
    fieldMask: ['StatusId'],
  });

export const buildReimbursementSubmitRequest = (r: Pick<Reimbursement, 'id'>) =>
  Reimbursement.create({
    id: r.id,
    statusId: 2,
    fieldMask: ['StatusId'],
  });
